<template>
  <div>
    <v-container v-if="!this.isReady">
      <v-layout align-center justify-center row fill-height>
        <v-progress-circular
          indeterminate
          color="primary"
        ></v-progress-circular>
      </v-layout>
    </v-container>
    <template v-else>
      <v-container>
        <v-card class="elevation-12">
          <v-card-title class="headline font-weight-regular blue-grey white--text">
            <v-btn flat icon color="white" :to="{ name: 'ProjectDetail', params: { projectId: article.projectId }}">
              <v-icon>chevron_left</v-icon>
            </v-btn>
            Article - {{article.originalTitle}}
          </v-card-title>
          <v-card-text v-if="article.status === 'published'">
            <h2>Article publié sur site-creator</h2>
            <p class="font-weight-thin font-italic">Le {{article.publishedAt | formatDate('DD/MM/YYYY à HH:MM')}}</p>
            <p><a :href="article.publishedUrl" target="_blank">Voir</a></p>
            <p><a :href="article.publishedEditUrl" target="_blank">Admin siteCreator</a></p>
          </v-card-text>
          <v-card-text v-else>
            <v-form ref="form" v-model="valid" lazy-validation>
              <v-flex sm12 md7>
                <v-text-field
                  v-model="article.translatedTitle"
                  :rules="[v => !!v || 'Titre requis']"
                  label="Titre de l'article"
                  placeholder="Titre de l'article"
                  required
                  outline
                ></v-text-field>
              </v-flex>
              <v-flex sm12 md7>
              <v-autocomplete
                v-model="article.sitecreatorCategory"
                :items="categories"
                :rules="[v => !!v || 'Catégorie requise']"
                label="Catégorie sitecreator"
                item-text="name"
                item-value="id"
                required
                outline
              ></v-autocomplete>
            </v-flex>
              <v-flex sm12 md7>
                <v-autocomplete
                  v-model="article.sitecreatorSite"
                  :items="sites"
                  label="Site de publication"
                  :rules="[v => !!v || 'Site requis']"
                  item-text="url"
                  item-value="url"
                  required
                  outline
                ></v-autocomplete>
              </v-flex>
              <v-flex sm12 md7 v-if="articleCategories.length > 0">
                <v-autocomplete
                  v-model="article.sitecreatorArticleCategory"
                  :items="articleCategories"
                  label="Catégorie de l'article"
                  :rules="[v => !!v || 'Catégorie requise']"
                  item-text="name"
                  item-value="id"
                  required
                  outline
                ></v-autocomplete>
              </v-flex>
              <v-flex sm12 md7>
                <v-text-field
                  v-model="article.translatedImage"
                  label="Url de l'image"
                  placeholder="Url de l'image"
                  outline
                ></v-text-field>
              </v-flex>
              <v-img
                v-if="article.translatedImage"
                :src="article.translatedImage"
                aspect-ratio="1"
                height="200"
                width="200"
                class="grey lighten-2"
              >
                <template v-slot:placeholder>
                <v-layout
                  fill-height
                  align-center
                  justify-center
                  ma-0
                >
                  <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                </v-layout>
              </template>
              </v-img>
              <div class="mt-5">
                <v-textarea
                  outline
                  v-model="article.translatedExcerpt"
                  label="Description"
                ></v-textarea>
              </div>
              <div class="mt-5">
                <editor api-key="ij1sljc3alhc0t7xhn6uhgrld76vlgxbnzt6vulzan488nko" v-model="article.translatedContent" required :init="settings"></editor>
              </div>
              <div class="mt-5" v-if="!displayKillduplicateButton">
                <b>Pourcentage de similitude (Killduplicate) : {{article.killduplicateStatus}} %</b>
              </div>
            </v-form>
          </v-card-text>
          <v-card-actions  v-if="article.status !== 'published'">
            <v-spacer></v-spacer>
            <v-btn v-if="displayKillduplicateButton" color="info" :loading="killduplicateLoading" @click.prevent.native="launchKillduplicate">Killduplicate</v-btn>
            <v-btn color="success" :loading="submitDraftLoading" @click.prevent.native="saveAsDraft">Enregistrer l'article (brouillon)</v-btn>
            <v-btn color="info" :loading="submitLoading" :disabled="!valid" @click.prevent.native="submit">Publier sur site-creator</v-btn>
          </v-card-actions>
        </v-card>
      </v-container>
    </template>
  </div>
</template>

<script>
  import Editor from '@tinymce/tinymce-vue';
  import { mapActions } from 'vuex';
  import get from 'lodash/get';
  import pick from 'lodash/pick';

  export default {
    components: { editor: Editor },
    props: ['articleId'],
    data() {
      return {
        killduplicateInterval: undefined,
        submitLoading: false,
        submitDraftLoading: false,
        valid: false,
        isReady: false,
        categories: [],
        sites: [],
        articleCategories: [],
        article: undefined,
        killduplicateProperties: [
          'killduplicateId',
          'killduplicateStatus',
          'killduplicateDuplicate',
          'killduplicatePercentage',
          'killduplicateResponse',
        ],
        settings: {
          height: 500,
          selector: 'textarea.redactable',
          menubar: false,
          toolbar: 'code undo redo | styleselect | bold italic | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image',
          resize: false,
        }
      };
    },
    computed: {
      submitDisabled() {
        return get(this.$refs.form)
      },
      killduplicateLoading() {
        return get(this, 'article.killduplicateStatus') === 'pending';
      },
      displayKillduplicateButton() {
        const kdStatus = get(this, 'article.killduplicateStatus');
        return kdStatus === null || kdStatus === 'pending';
      }
    },
    async destroyed() {
      clearInterval(this.interval);
    },
    methods: {
      ...mapActions({
        snackbar: 'snackbar',
      }),
      async fetchKillduplicateStatus() {
        const response = await this.$http.get(`/api/articles/killduplicate-status/${this.articleId}`);
        this.killduplicateProperties.forEach(prop => {
          this.article[prop] = response.data[prop];
        });
      },
      async launchKillduplicate() {
        const response = await this.$http.get(`/api/articles/killduplicate-add/${this.articleId}`);
        this.killduplicateProperties.forEach(prop => {
          this.article[prop] = response.data[prop];
        });
      },
      async fetchArticle() {
        const response = await this.$http.get(`/api/articles/${this.articleId}`);
        this.article = response.data;
      },
      async fetchCategories() {
        const response = await this.$http.get('/api/categories/list');
        this.categories = response.data;
      },
      async fetchArticleCategories(site) {
        const response = await this.$http.post('/api/categories/articles/list', { url: site });
        this.articleCategories = get(response, 'data.categories', []);
      },
      async fetchSites() {
        const response = await this.$http.get(`/api/sites-by-category/${this.article.sitecreatorCategory}`);
        this.sites = response.data;
      },
      async submit() {
        if (this.$refs.form.validate()) {
          try {
          this.submitLoading = true;
          const data = pick(this.article, [
            'sitecreatorCategory',
            'sitecreatorArticleCategory',
            'sitecreatorSite',
            'translatedContent',
            'translatedImage',
            'translatedTitle',
            'translatedExcerpt',
          ]);
          const response = await this.$http.post(`/api/articles/save-and-publish/${this.articleId}`, data);
          this.snackbar({  color: 'success', text: 'Enregistré et publié'});
          this.article = response.data;
          this.submitLoading = false;
          } catch(e) {
            this.snackbar({  color: 'error', text: 'Erreur lors de la publication'});
            this.submitLoading = false;
          }
        } else {
          this.snackbar({  color: 'error', text: 'Veuillez corriger les erreurs dans le forumlaire.'});
        }
      },

      async saveAsDraft() {
        this.submitDraftLoading = true;
        try {
          const data = pick(this.article, [
            'sitecreatorCategory',
            'sitecreatorArticleCategory',
            'sitecreatorSite',
            'translatedContent',
            'translatedImage',
            'translatedTitle',
            'translatedExcerpt',
          ]);
          const response = await this.$http.post(`/api/articles/save-as-draft/${this.articleId}`, data);
          this.snackbar({  color: 'success', text: 'Enregistré en tant que brouillon'});
          this.article = response.data;
          this.submitDraftLoading = false;
        } catch(e) {
          this.snackbar({  color: 'error', text: "Erreur lors de l'enregistrement"});
          this.submitDraftLoading = false;
        }
      }
    },
    watch: {
      killduplicateLoading: {
        async handler(value) {
          if (value) {
            this.killduplicateInterval = setInterval( () => {
              this.fetchKillduplicateStatus()
            }, 4000)
          } else {
            clearInterval(this.killduplicateInterval);
            this.killduplicateInterval = undefined;
          }
        },
        immediate: true,
      },
      articleId: {
        async handler() {
          await this.fetchArticle();
          await this.fetchCategories();
          await this.fetchSites();
          this.isReady = true;
        },
        immediate: true,
      },
      'article.sitecreatorCategory': {
        async handler() {
          await this.fetchSites();
          this.sitecreatorSite = undefined;
        },
        immediate: false,
      },
      'article.sitecreatorSite': {
        async handler(value) {
          if (value) {
            await this.fetchArticleCategories(value);
          } else {
            this.articleCategories = [];
          }
        },
        immediate: false,
      }
    }
  }
</script>

<style scoped lang="scss">
</style>
